import { memo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Divider, Grid2, IconButton, Typography } from "@mui/material";
import { CalendarMonth, Delete, Edit, EditNote, Leaderboard } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import Accordion from "../components/Accordion.js";
import Form from "../components/Form.js";
import Spinner from "../components/Spinner.js";
import Tooltip from "../components/Tooltip.js";
import Dialog from "../components/Dialog.js";
import Popup from "../components/Popup.js";
import Search from "../components/Search.js";
import { ThirdBackgroundButton } from "../components/Buttons.js";
import { dayjs, isFuzzyMatch, useSnackbar } from "../utils/index.js";
import {
	createTask,
	getAvailableCategories,
	getAvailableGoalDSLs,
	getAvailableSubcategories,
	getTasksAdmin,
	removeTaskAdmin,
	removeCategoryAdmin,
	removeSubcategoryAdmin,
	updateCategoryAdmin,
	updateSubcategoryAdmin,
	editTaskAdmin,
	updateCategoryDatesAdmin,
	updateSubcategoryDatesAdmin,
} from "../api/index.js";

const useStyles = makeStyles((theme) => ({
	deleteIcon: {
		color: theme.palette.error.main,
	},
	editIcon: {
		color: theme.palette.primary.main,
	},
	leadIcon: {
		color: theme.palette.secondary.main,
	},
}));

const Tasks = () => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { error, success } = useSnackbar();
	const [availableGoalDSLs, setAvailableGoalDSLs] = useState([]);
	const [availableCategories, setAvailableCategories] = useState([]);
	const [availableSubcategories, setAvailableSubcategories] = useState([]);
	const [tasks, setTasks] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchFilter, setSearchFilter] = useState("");
	const [displayTasks, setDisplayTasks] = useState([]);
	const [removeCategoryWarningOpen, setRemoveCategoryWarningOpen] = useState(null);
	const [removeSubcategoryWarningOpen, setRemoveSubcategoryWarningOpen] = useState(null);
	const [categoryNamePopupOpen, setCategoryNamePopupOpen] = useState(null);
	const [subcategoryNamePopupOpen, setSubcategoryNamePopupOpen] = useState(null);
	const [removeTaskWarningOpen, setRemoveTaskWarningOpen] = useState(null);
	const [editTaskPopupOpen, setEditTaskPopupOpen] = useState(null);
	const [createTaskPopupOpen, setCreateTaskPopupOpen] = useState(false);
	const [categoryDatesPopupOpen, setCategoryDatesPopupOpen] = useState(null);
	const [subcategoryDatesPopupOpen, setSubcategoryDatesPopupOpen] = useState(null);

	const formRef = useRef(null);
	const categoryNameFormRef = useRef(null);
	const subcategoryNameFormRef = useRef(null);
	const editTaskFormRef = useRef(null);
	const datesRef = useRef(null);

	const fetchTasks = async () => {
		setIsLoading(true);
		const { success: fetchSuccess, tasks: fetchedTasks, message } = await getTasksAdmin();
		if (fetchSuccess) {
			setTasks(fetchedTasks);
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const fetchAvailableGoalDSLs = async () => {
		setIsLoading(true);
		const { success: fetchSuccess, goalDSLModels: fetchedGoalDSLs, message } = await getAvailableGoalDSLs();
		if (fetchSuccess) {
			setAvailableGoalDSLs(fetchedGoalDSLs);
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const fetchAvailableCategories = async () => {
		setIsLoading(true);
		const { success: fetchSuccess, categories: fetchedCategories, message } = await getAvailableCategories();
		if (fetchSuccess) {
			setAvailableCategories(fetchedCategories);
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const fetchAvailableSubcategories = async () => {
		setIsLoading(true);
		const { success: fetchSuccess, subcategories: fetchedSubcategories, message } = await getAvailableSubcategories();
		if (fetchSuccess) {
			setAvailableSubcategories(fetchedSubcategories);
		} else {
			error(message);
		}

		setIsLoading(false);
	};
	
	const onSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: createSuccess, message, errors } = await createTask(values);
			if (createSuccess) {
				success(message);
				setCreateTaskPopupOpen(false);
				fetchTasks();
			} else if (formRef.current) {
				error(message);
				for (const err of errors) {
					formRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onCategorySubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: submitSuccess, message } = await updateCategoryAdmin(categoryNamePopupOpen.name, values);
			if (submitSuccess) {
				success(message);
				setCategoryNamePopupOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onSubcategorySubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: submitSuccess, message } = await updateSubcategoryAdmin(subcategoryNamePopupOpen.name, values);
			if (submitSuccess) {
				success(message);
				setSubcategoryNamePopupOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onRemoveCategory = async () => {
		setIsLoading(true);

		try {
			const { success: removeSuccess, message } = await removeCategoryAdmin(removeCategoryWarningOpen.name);
			if (removeSuccess) {
				success(message);
				setRemoveCategoryWarningOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onRemoveSubcategory = async () => {
		setIsLoading(true);

		try {
			const { success: removeSuccess, message } = await removeSubcategoryAdmin(removeSubcategoryWarningOpen.name);
			if (removeSuccess) {
				success(message);
				setRemoveSubcategoryWarningOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onRemoveTask = async () => {
		setIsLoading(true);

		try {
			const taskId = removeTaskWarningOpen.id;
			const { success: removeSuccess, message } = await removeTaskAdmin(taskId);
			if (removeSuccess) {
				success(message);
				setRemoveTaskWarningOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onEdit = async (values) => {
		setIsLoading(true);

		try {
			const taskId = editTaskPopupOpen.id;
			const { success: editSuccess, message } = await editTaskAdmin(taskId, values);
			if (editSuccess) {
				success(message);
				setEditTaskPopupOpen(null);
				fetchTasks();
			} else {
				error(message);
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onDatesSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: submitSuccess, message } = await (categoryDatesPopupOpen
				? updateCategoryDatesAdmin
				: updateSubcategoryDatesAdmin)((categoryDatesPopupOpen || subcategoryDatesPopupOpen).name, values);
			if (submitSuccess) {
				success(message);
				setCategoryDatesPopupOpen(null);
				setSubcategoryDatesPopupOpen(null);
				fetchTasks();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onCreateTask = async () => {
		await fetchAvailableGoalDSLs();
		await fetchAvailableCategories();
		await fetchAvailableSubcategories();
		setCreateTaskPopupOpen(true);
	};

	const onEditTask = async (taskToEdit) => {
		await fetchAvailableGoalDSLs();
		await fetchAvailableCategories();
		await fetchAvailableSubcategories();
		setEditTaskPopupOpen(taskToEdit);
	};

	const formContent = [
		{
			customType: "input",
			id: "title",
			type: "text",
			label: "Title",
			placeholder: "Title",
		},
		{
			customType: "autocomplete",
			id: "category",
			filled: false,
			placeholder: "Category",
			label: null,
			allowCustomInput: true,
			options: availableCategories,
		},
		{
			customType: "autocomplete",
			id: "subcategory",
			filled: false,
			placeholder: "Subcategory",
			label: null,
			allowCustomInput: true,
			options: availableSubcategories,
		},
		{
			customType: "date-picker",
			id: "openAt",
			label: "Open At",
			type: "desktop",
		},
		{
			customType: "date-picker",
			id: "closeAt",
			label: "Close At",
			type: "desktop",
		},
		{
			customType: "autocomplete",
			id: "goalDSLModel",
			filled: false,
			placeholder: "Goal DSL Model",
			label: null,
			allowCustomInput: false,
			options: availableGoalDSLs.map((goalDSL) => goalDSL.title),
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const categoryNameFormContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Category Name",
			value: categoryNamePopupOpen?.name,
			placeholder: "Category Name",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const subcategoryNameFormContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Subcategory Name",
			value: subcategoryNamePopupOpen?.name,
			placeholder: "Subcategory Name",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const editFormContent = [
		{
			customType: "input",
			id: "title",
			type: "text",
			label: "Title",
			value: editTaskPopupOpen?.title,
			placeholder: "Title",
		},
		{
			customType: "autocomplete",
			id: "category",
			filled: false,
			placeholder: "Category",
			label: null,
			allowCustomInput: true,
			options: availableCategories,
			defaultValue: editTaskPopupOpen?.category,
		},
		{
			customType: "autocomplete",
			id: "subcategory",
			filled: false,
			placeholder: "Subcategory",
			label: null,
			allowCustomInput: true,
			options: availableSubcategories,
			defaultValue: editTaskPopupOpen?.subcategory,
		},
		{
			customType: "date-picker",
			id: "openAt",
			label: "Open At",
			type: "desktop",
			value: dayjs(editTaskPopupOpen?.openAt),
		},
		{
			customType: "date-picker",
			id: "closeAt",
			label: "Close At",
			type: "desktop",
			value: dayjs(editTaskPopupOpen?.closeAt),
		},
		{
			customType: "autocomplete",
			id: "goalDSLModel",
			filled: false,
			placeholder: "Goal DSL Model",
			label: null,
			allowCustomInput: false,
			options: availableGoalDSLs.map((goalDSL) => goalDSL.title),
			defaultValue: editTaskPopupOpen?.goalDSLModel,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const editDatesFormContent = [
		{
			customType: "date-picker",
			id: "openAt",
			label: "Open At",
			type: "desktop",
		},
		{
			customType: "date-picker",
			id: "closeAt",
			label: "Close At",
			type: "desktop",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	useEffect(() => {
		fetchTasks();
	}, []);

	useEffect(() => {
		fetchAvailableGoalDSLs();
	}, []);

	useEffect(() => {
		fetchAvailableCategories();
	}, []);

	useEffect(() => {
		fetchAvailableSubcategories();
	}, []);

	useEffect(() => {
		const filterTasks = (tasksToFilter) => tasksToFilter.filter((category) => isFuzzyMatch(category.name, searchFilter)
		|| category.subcategories.some((subcategory) => isFuzzyMatch(subcategory.name, searchFilter))
		|| category.subcategories.some((subcategory) => subcategory.tasks.some((task) => isFuzzyMatch(task.title, searchFilter))));
		setDisplayTasks(filterTasks(tasks));
	}, [searchFilter, tasks]);
	
	return (
		<>
			<Spinner open={isLoading} />
			<Grid2 container display="flex" direction="column" justifyContent="center" alignItems="center">
				<Grid2 width="100%" display="flex" justifyContent="space-between">
					<ThirdBackgroundButton onClick={onCreateTask} title="Create Task" />
					<Search width="500px" value={searchFilter} onChange={(event) => setSearchFilter(event.target.value)} />
				</Grid2>
				{displayTasks.map((category) => (
					<Grid2 key={category?.name} mt={2} width="100%">
						<Accordion
							alwaysExpanded
							title={(
								<Grid2
									container
									width="100%"
									display="flex"
									direction="row"
									alignItems="center"
									justifyContent="space-between"
								>
									<Typography>{category.name}</Typography>
									<Grid2>
										<Tooltip title="Edit Category Name">
											<IconButton onClick={() => setCategoryNamePopupOpen(category)}>
												<EditNote />
											</IconButton>
										</Tooltip>
										<Tooltip title="Edit Category Dates">
											<IconButton onClick={() => {
												setSubcategoryDatesPopupOpen(null);
												setCategoryDatesPopupOpen(category);
											}}>
												<CalendarMonth />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete Category">
											<IconButton onClick={() => setRemoveCategoryWarningOpen(category)}>
												<Delete />
											</IconButton>
										</Tooltip>
									</Grid2>
								</Grid2>
							)}
							content={(
								<Grid2 container width="100%" display="flex" direction="column" alignItems="center" justifyContent="center" spacing={0} color="black">
									{category.subcategories.map((subcategory, ind) => (
										<Grid2 key={subcategory?.name} mt={ind === 0 ? 1 : 2} width="100%">
											<Accordion
												alwaysExpanded
												key={subcategory?.name}
												titleBackground="info"
												title={(
													<Grid2
														container
														width="100%"
														display="flex"
														direction="row"
														alignItems="center"
														justifyContent="space-between"
													>
														<Typography>{subcategory.name}</Typography>
														<Grid2>
															<Tooltip title="Edit Subcategory Name">
																<IconButton onClick={() => setSubcategoryNamePopupOpen(subcategory)}>
																	<EditNote />
																</IconButton>
															</Tooltip>
															<Tooltip title="Edit Subcategory Dates">
																<IconButton onClick={() => {
																	setCategoryDatesPopupOpen(null);
																	setSubcategoryDatesPopupOpen(subcategory);
																}}>
																	<CalendarMonth />
																</IconButton>
															</Tooltip>
															<Tooltip title="Delete Subcategory">
																<IconButton onClick={() => setRemoveSubcategoryWarningOpen(subcategory)}>
																	<Delete />
																</IconButton>
															</Tooltip>
														</Grid2>
													</Grid2>
												)}
												subtitle={(
													<Grid2 container display="flex" direction="row" alignItems="center" justifyContent="center" spacing={0}>
														<Grid2 size={{ xs: 2 }}>
															<Typography>{"Title"}</Typography>
														</Grid2>
														<Grid2 size={{ xs: 2 }} textAlign="center">
															<Typography>{"Score"}</Typography>
														</Grid2>
														<Grid2 size={{ xs: 2 }} textAlign="center">
															<Typography>{"Teams Solved"}</Typography>
														</Grid2>
														<Grid2 size={{ xs: 2 }} textAlign="center">
															<Typography>{"Open"}</Typography>
														</Grid2>
														<Grid2 size={{ xs: 3 }} textAlign="center">
															<Typography>{"GoalDSL Model"}</Typography>
														</Grid2>
														<Grid2 size={{ xs: 1 }} />
													</Grid2>
												)}
												content={(
													<Grid2 container width="100%" display="flex" direction="column" alignItems="center" justifyContent="center" spacing={0} color="black">
														{subcategory.tasks.map((tsk, indx) => (
															<Grid2 key={`comp_${indx}`} flexDirection="column" width="100%">
																{indx !== 0
																&& (
																	<Grid2 key={`divider_${indx}`} my={1} width="100%">
																		<Divider style={{ width: "100%" }} />
																	</Grid2>
																)}
																<Grid2 key={`row_${indx}`} container alignItems="center">
																	<Grid2 size={{ xs: 2 }}>
																		<Typography>{tsk.title}</Typography>
																	</Grid2>
																	<Grid2 size={{ xs: 2 }} textAlign="center">
																		<Typography>{tsk.score}</Typography>
																	</Grid2>
																	<Grid2 size={{ xs: 2 }} textAlign="center">
																		<Typography>{tsk.perfectScores}</Typography>
																	</Grid2>
																	<Grid2 size={{ xs: 2 }} textAlign="center">
																		<Typography>{`${dayjs(tsk.openAt).format("DD/MM/YYYY")}-${dayjs(tsk.closeAt).format("DD/MM/YYYY")}`}</Typography>
																	</Grid2>
																	<Grid2 size={{ xs: 3 }} textAlign="center">
																		<Typography>{tsk.goalDSLModel}</Typography>
																	</Grid2>
																	<Grid2 size={{ xs: 1 }} justifyContent="flex-end" display="flex">
																		<Tooltip title="Edit Task">
																			<IconButton
																				onClick={() =>onEditTask({
																					...tsk, category: category.name, subcategory: subcategory.name,
																				})}
																			>
																				<Edit className={classes.editIcon} />
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Remove Task">
																			<IconButton onClick={() => setRemoveTaskWarningOpen(tsk)}>
																				<Delete className={classes.deleteIcon} />
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="See Leaderboard">
																			<IconButton onClick={() => navigate(`../leaderboard/${tsk.id}`)}>
																				<Leaderboard className={classes.leadIcon} />
																			</IconButton>
																		</Tooltip>
																	</Grid2>
																</Grid2>
															</Grid2>
														))}
													</Grid2>
												)}
											/>
										</Grid2>
									))}
								</Grid2>
							)}
						/>
					</Grid2>
				))}
				{tasks.length === 0 && (
					<Grid2 container direction="column" alignItems="center" justifyContent="center">
						<Typography variant="h5" sx={{ color: "primary.main", p: 1 }}>{"No tasks found!"}</Typography>
					</Grid2>
				)}
			</Grid2>
			<Dialog
				open={removeCategoryWarningOpen !== null}
				title="Delete Category"
				text={`Are you sure you want to delete the category ${removeCategoryWarningOpen?.name}? All subcategories and tasks will be lost.`}
				confirmButton="Delete"
				cancelButton="Cancel"
				onConfirm={onRemoveCategory}
				onClose={() => setRemoveCategoryWarningOpen(null)}
			/>
			<Dialog
				open={removeSubcategoryWarningOpen !== null}
				title="Delete Subcategory"
				text={`Are you sure you want to delete the subcategory ${removeSubcategoryWarningOpen?.name}? All tasks will be lost.`}
				confirmButton="Delete"
				cancelButton="Cancel"
				onConfirm={onRemoveSubcategory}
				onClose={() => setRemoveSubcategoryWarningOpen(null)}
			/>
			<Dialog
				open={removeTaskWarningOpen !== null}
				title="Delete Task"
				text={`Are you sure you want to delete task ${removeTaskWarningOpen?.title}?`}
				confirmButton="Delete"
				cancelButton="Cancel"
				onConfirm={onRemoveTask}
				onClose={() => setRemoveTaskWarningOpen(null)}
			/>
			<Popup
				width="400px"
				title="Edit Category Name"
				open={categoryNamePopupOpen !== null}
				onClose={() => setCategoryNamePopupOpen(null)}
			>
				<Form enableReinitialize ref={categoryNameFormRef} content={categoryNameFormContent} validationSchema="categoryName" onSubmit={onCategorySubmit} />
			</Popup>
			<Popup
				width="400px"
				title="Edit Subcategory Name"
				open={subcategoryNamePopupOpen !== null}
				onClose={() => setSubcategoryNamePopupOpen(null)}
			>
				<Form enableReinitialize ref={subcategoryNameFormRef} content={subcategoryNameFormContent} validationSchema="subcategoryName" onSubmit={onSubcategorySubmit} />
			</Popup>
			<Popup
				width="600px"
				title="Create Task"
				open={createTaskPopupOpen}
				onClose={() => setCreateTaskPopupOpen(false)}
			>
				<Form ref={formRef} content={formContent} validationSchema="taskCreation" onSubmit={onSubmit} />
			</Popup>
			<Popup
				width="600px"
				title="Edit Task"
				open={editTaskPopupOpen !== null}
				onClose={() => setEditTaskPopupOpen(null)}
			>
				<Form ref={editTaskFormRef} content={editFormContent} validationSchema="taskCreation" onSubmit={onEdit} />
			</Popup>
			<Popup
				width="600px"
				title={`Edit ${categoryDatesPopupOpen ? "Category" : "Subcategory"} Dates`}
				open={categoryDatesPopupOpen !== null || subcategoryDatesPopupOpen !== null}
				onClose={() => {
					setCategoryDatesPopupOpen(null);
					setSubcategoryDatesPopupOpen(null);
				}}
			>
				<Form ref={datesRef} content={editDatesFormContent} validationSchema="dates" onSubmit={onDatesSubmit} />
			</Popup>
		</>
	);
};

export default memo(Tasks);
